import { Node, mergeAttributes, nodeInputRule, nodePasteRule } from "@tiptap/core";

export interface BlipTokenOptions {
  HTMLAttributes: Record<string, unknown>;
}

export const BLIP_TOKEN_NAME = "blipToken";

/** `{@키}` 패턴(전역). 키 = 공백·중괄호·@ 제외 1+ 문자. */
export const TOKEN_PATTERN = /\{@([^{}\s@]+)\}/g;

const escapeAttr = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/**
 * 인라인 토큰 노드.
 * - 편집기: atom 칩(시각 NodeView는 바인딩 레이어에서 주입).
 * - 직렬화(renderHTML): `<span data-blip-token="키">{@키}</span>` — 내부 텍스트 `{@키}`가
 *   서버 vote.service applyTabTokens 정규식과 1:1 매칭(계약).
 * - 저장 직전 detokenizeHtml로 span 제거 → 순수 `{@키}` 텍스트로 보관(서버 계약 형태).
 */
export const BlipToken = Node.create<BlipTokenOptions>({
  name: BLIP_TOKEN_NAME,
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addOptions() {
    return { HTMLAttributes: {} };
  },

  addAttributes() {
    return {
      key: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-blip-token") ?? "",
        renderHTML: (attrs) => (attrs.key ? { "data-blip-token": attrs.key } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-blip-token]" }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const key = String(node.attrs.key ?? "");
    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { class: "blip-token" }),
      `{@${key}}`,
    ];
  },

  renderText({ node }) {
    return `{@${node.attrs.key ?? ""}}`;
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: /\{@([^{}\s@]+)\}$/,
        type: this.type,
        getAttributes: (match) => ({ key: match[1] }),
      }),
    ];
  },

  addPasteRules() {
    return [
      nodePasteRule({
        find: TOKEN_PATTERN,
        type: this.type,
        getAttributes: (match) => ({ key: match[1] }),
      }),
    ];
  },
});

/** 순수 `{@키}` 텍스트 → `<span data-blip-token>`(편집기 로드용). allowedKeys 지정 시 화이트리스트만 변환. */
export function tokenizeHtml(html: string, allowedKeys?: readonly string[]): string {
  return html.replace(TOKEN_PATTERN, (full, key: string) => {
    if (allowedKeys && !allowedKeys.includes(key)) return full;
    return `<span data-blip-token="${escapeAttr(key)}" class="blip-token">${full}</span>`;
  });
}

/** `<span data-blip-token="키">…</span>` → 순수 `{@키}` 텍스트(저장·서버 계약 형태). */
export function detokenizeHtml(html: string): string {
  return html.replace(
    /<span\b[^>]*\bdata-blip-token="([^"]*)"[^>]*>[\s\S]*?<\/span>/g,
    (_full, key: string) => `{@${key}}`,
  );
}
